import { User } from "@soft-db/monitoring-api-client";
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { RouteLocationGeneric } from "vue-router";
import { useCurrentUser, useCurrentUserScope } from "../api/current-user";
import { __SOFTDB_ID__ } from "../constants";
import { usePermissionStore } from "./permission";
import { useScopeStore } from "./scope";

export type AppFeatures = {
	dashboard: boolean;
	integrity: boolean;

	site: boolean;
	station: boolean;
	space: boolean;
	report: boolean;

	favorite: boolean;

	audit: boolean;

	scopeSettings: boolean;
	userSettings: boolean;

	publicApi: boolean;
	privateApi: boolean;
};
function AppFeatures(): AppFeatures {
	return {
		dashboard: false,
		integrity: false,
		site: false,
		station: false,
		space: false,
		report: false,
		favorite: false,
		audit: false,
		scopeSettings: false,
		userSettings: false,
		publicApi: false,
		privateApi: false,
	};
}

export type FeatureType = keyof AppFeatures;

export const useFeaturesStore = defineStore("features", () => {
	const { hasPermission } = usePermissionStore();

	const { scope: currentUserScope } = useCurrentUserScope();
	const { scope: viewScope } = storeToRefs(useScopeStore());

	const { isPublic, currentUser } = useCurrentUser();

	type ScopeChecker = (type: string) => boolean;

	function userHasFeature(feature: keyof User["features"]): boolean {
		if (currentUser.value == null) {
			return false;
		}

		return currentUser.value.features[feature];
	}

	const rules: Record<FeatureType, (isScope: ScopeChecker) => boolean> = {
		dashboard: () => hasPermission("dashboard.get") && userHasFeature("dashboard"),

		integrity: () => hasPermission("integrity.get") && userHasFeature("integrity"),

		site: () => hasPermission("site.get"),

		station: () => hasPermission("station.get"),

		report: () => hasPermission("report.get") && userHasFeature("report"),

		space: (isScope) => hasPermission("space.get") && isScope("distributor"),

		favorite: (isScope) => isScope("distributor") && !isPublic.value,

		audit: () => hasPermission("audit.get") && userHasFeature("audit"),

		scopeSettings: (isScope) => {
			if (isScope("distributor")) {
				return hasPermission("distributor.update");
			}
			if (isScope("space")) {
				return hasPermission("space.update") || hasPermission("user.create");
			}

			return false;
		},

		userSettings: () => !isPublic.value,

		publicApi: () => hasPermission("public-api.get"),
		privateApi: () => hasPermission("private-api.get"),
	};

	const features = computed(() => {
		const features = AppFeatures();

		const isScope: ScopeChecker = (type) => viewScope.value.type === type;

		for (const key of Object.keys(rules)) {
			features[key] = rules[key](isScope);
		}

		return features;
	});

	const currentUserFeatures = computed(() => {
		const features = AppFeatures();

		const isScope: ScopeChecker = (type) => currentUserScope.value?.type === type;

		for (const key of Object.keys(rules)) {
			features[key] = rules[key](isScope);
		}

		return features;
	});

	const userIsSoftdB = computed(() => {
		if (currentUser.value == null) {
			return false;
		}

		const { scope } = currentUser.value;
		return scope.type === "distributor" && scope.id === __SOFTDB_ID__;
	});

	function hasFeature(feature: FeatureType) {
		return features.value[feature];
	}

	function currentUserHasFeature(feature: FeatureType) {
		return currentUserFeatures.value[feature];
	}

	/**
	 * `user` use the scope of the registered user
	 * `view` use the scope of the current view, taking overwrite in account.
	 */
	type RouteCheckScopeType = "user" | "view";

	function isRouteActive(route: RouteLocationGeneric, type: RouteCheckScopeType = "view"): boolean {
		const { feature, softdBOnly } = route.meta;

		if (softdBOnly && !userIsSoftdB.value) {
			return false;
		}

		if (feature == null) {
			return true;
		}

		switch (type) {
			case "user":
				return currentUserHasFeature(feature);
			case "view":
				return hasFeature(feature);
		}
	}

	return {
		features,
		currentUserFeatures,
		hasFeature,
		currentUserHasFeature,
		isRouteActive,
	};
});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useFeaturesStore, import.meta.hot));
}
